import {
  fetchAuthSession,
  fetchUserAttributes,
  signIn,
  signOut,
  updateUserAttributes,
} from "aws-amplify/auth";
import { get, size } from "lodash";
import { DEFAULT_ERROR_MESSAGE } from "../constants";
import { verifyDeviceToken } from "./axle";

/**
 * Checks if user is authenticated with Cognito and validates device token
 * @param session - Session object containing ignition token
 * @returns Promise with authentication data and token validation result
 */
export const checkAuthenticatedUser = async (session: any) => {
  /*
   * ===== Test utility escape hatch =====
   * Playwright E2E tests occasionally need to bypass Cognito entirely and provide
   * a mocked authentication response so that the UI can render the "Welcome back"
   * flow without relying on Amplify or network calls. If a test script defines
   * `window.__MOCK_CHECK_AUTHENTICATED_USER__`, short-circuit the normal logic
   * and immediately return the supplied object.
   *
   * This global is ONLY set by tests via `page.addInitScript` – it will be
   * undefined in all production builds so the standard authentication logic
   * continues to run untouched.
   */
  if (
    typeof window !== "undefined" &&
    (window as any).__MOCK_CHECK_AUTHENTICATED_USER__ !== undefined
  ) {
    return (window as any).__MOCK_CHECK_AUTHENTICATED_USER__;
  }

  try {
    const { tokens } = await fetchAuthSession({ forceRefresh: true });

    const ignitionToken = session.id;

    // If no ID token is present, treat as unauthenticated without throwing
    if (!tokens?.idToken) {
      console.log("No signed-in user found...");
      return { isAuthenticated: false, tokens: null, ruxTokenData: null };
    }
    // Verify the device token and get the Rux token data
    const result = await verifyDeviceToken(
      tokens.idToken.toString(),
      ignitionToken
    );

    // Check if result is undefined or not an array
    if (!result || !Array.isArray(result)) {
      console.error("Invalid result from verifyDeviceToken:", result);
      return { isAuthenticated: false, tokens: null, ruxTokenData: null };
    }

    const [data, error] = result;

    if (error || !data || !data.isValid) {
      console.error("Token validation failed:", error?.message);
      return { isAuthenticated: false, tokens: null, ruxTokenData: null };
    }

    // The existing token is valid and the user is authenticated
    console.log("Existing authenticated user found...");
    return {
      isAuthenticated: true,
      tokens,
      ruxTokenData: data,
    };
  } catch (err: any) {
    // Unexpected error – log to Sentry via console.error
    console.error("Cognito auth error:", err.name);
    console.log(JSON.stringify(err, null, 2));
    return { isAuthenticated: false, tokens: null, ruxTokenData: null };
  }
};

/**
 * Clear any existing user session from Amplify Auth
 */
export const clearLocalSession = async (): Promise<void> => {
  try {
    await signOut({ global: true });
  } catch (_) {
    /* not signed-in – ignore */
  }
};

/**
 * Handle the sign-in process for returning users
 * @param phoneNumber - The user's phone number for authentication
 * @param nextStep - Function to navigate to the next step in the auth flow
 * @param setError - Function to set error message
 * @param setIsSubmitting - Function to update submission state
 */
export async function handleReturningUserSignIn(
  phoneNumber: string,
  nextStep: (step: string) => void,
  setError: (message: string) => void,
  setIsSubmitting: (isSubmitting: boolean) => void
): Promise<void> {
  try {
    // Clear any existing session
    await clearLocalSession();
    // Attempt to sign in with the phone number
    const { nextStep: signInNextStep } = await signIn({
      username: phoneNumber,
      options: {
        authFlowType: "USER_AUTH",
        preferredChallenge: "SMS_OTP",
        preferredFirstFactor: "SMS_OTP",
      },
    });

    if (signInNextStep.signInStep === "CONFIRM_SIGN_IN_WITH_SMS_CODE") {
      if (signInNextStep.codeDeliveryDetails?.deliveryMedium === "SMS") {
        console.log(
          "SMS OTP sent to",
          signInNextStep.codeDeliveryDetails.destination
        );
        nextStep("enter-otp");
      } else {
        setError(
          "Unable to send verification code. Please check your phone number and try again."
        );
      }
    } else if (signInNextStep.signInStep === "CONFIRM_SIGN_UP") {
      // This is step occurs when a user has attempted to sign up but did not complete OTP verification
      setError(
        "No account found with this phone number. Please continue as guest to create an account."
      );
    } else {
      console.error("Unexpected sign-in step:", signInNextStep.signInStep);
      setError(
        "Verification failed. Please check your phone number and try again."
      );
    }
  } catch (err: any) {
    if (err.name === "UserNotFoundException") {
      // Expected case when a phone number isn't linked to an account – don't log as error
      setError(
        "This phone number isn't registered to an Axle account. Please continue as guest."
      );
    } else if (
      err.name === "InvalidParameterException" ||
      err.name === "ValidationException"
    ) {
      setError(
        "Invalid phone number format. The number may not be capable of receiving SMS."
      );
    } else if (err.message && err.message.includes("SMS")) {
      setError(
        "Unable to send verification SMS. Please verify your phone number is correct."
      );
    } else if (err.name === "NetworkError") {
      console.log("Network error... asking user to try again");
      setError(DEFAULT_ERROR_MESSAGE);
    } else {
      // Unexpected error – log to Sentry via console.error
      console.error("Sign-in failed →", err.name);
      console.log(JSON.stringify(err, null, 2));
      setError(
        "Something went wrong. Please check your phone number and try again."
      );
    }
  } finally {
    setIsSubmitting(false);
  }
}

/**
 * Updates the user's saved accounts list in Cognito with a new account
 * @param accountId - The ID of the account to be added to the user's saved accounts
 */
export const updateCognitoUserAccounts = async (
  accountId: string
): Promise<void> => {
  // fetch existing accounts from Cognito
  const user = await fetchUserAttributes();
  const existingAccounts = JSON.parse(
    get(user, "custom:accounts", "{}")
  ).accounts;
  console.log(
    `Existing accounts in Cognito: ${JSON.stringify(existingAccounts)}`
  );
  // check if the new account is already in the list
  const isAccountAlreadyLinked = existingAccounts.some(
    (account: { id: string }) => accountId === account.id
  );

  if (!isAccountAlreadyLinked) {
    console.log(
      `Adding new account ${accountId} to existing accounts list in Cognito...`
    );
    // Add the new account to the list
    const newTokenAccount = {
      id: accountId,
      scopes: ["account:link"],
      createdAt: new Date().toISOString(),
    };

    let updatedAccounts;

    if (size(existingAccounts) > 5) {
      // Find the oldest account by createdAt timestamp
      const oldestAccount = existingAccounts.reduce(
        (oldest: any, current: any) => {
          return new Date(oldest.createdAt) < new Date(current.createdAt)
            ? oldest
            : current;
        }
      );

      console.log(
        `Replacing oldest account ${oldestAccount.id} with new account ${accountId}`
      );

      // Replace the oldest account with the new one
      updatedAccounts = existingAccounts.map((account: any) =>
        account.id === oldestAccount.id ? newTokenAccount : account
      );
    } else if (size(existingAccounts) === 0) {
      // If no existing accounts, just add the new one
      updatedAccounts = [newTokenAccount];
    } else {
      // If there are existing accounts, append the new one
      updatedAccounts = [...existingAccounts, newTokenAccount];
    }

    // Update the user's attributes in Cognito with the updated accounts
    await updateUserAttributes({
      userAttributes: {
        "custom:accounts": JSON.stringify({
          accounts: updatedAccounts,
        }),
      },
    });
  }
};
